import React, { Fragment, ReactNode, isValidElement } from 'react';

import './grid.scss';

interface Props {
  children: ReactNode;
  className?: string;
  layout?: gridLayout;
  tabletLayout?: gridLayout;
  mobileLayout?: gridLayout;
  gutter?: gridGutter;
  tabletGutter?: gridGutter;
  mobileGutter?: gridGutter;
  vgutter?: gridGutter;
  paginate?: boolean;
  pageSize?: number;
}

export enum gridLayout {
  default = 'default',
  hub = 'hub',
  hubHero = 'hub-hero',
  spaceBetween = 'space-between',
  whole = 'whole',
  halves = 'halves',
  thirds = 'thirds',
  quarters = 'quarters'
}

export enum gridGutter {
  none = '',
  xsmall = 'xs',
  small = 's',
  medium = 'm',
  large = 'l',
  xlarge = 'xl'
}

const Grid = ({
  layout = gridLayout.default,
  tabletLayout = layout,
  mobileLayout = tabletLayout,
  gutter = gridGutter.medium,
  tabletGutter = gutter,
  mobileGutter = tabletGutter,
  vgutter = gutter,
  children,
  className
}: Props) => {
  const dataClassName = 'data-grid-class';
  const chunkArray = <T extends {} | undefined | null>(
    arr: T[],
    chunk: number
  ): T[][] => {
    const chunked = arr.reduce((accumulator: T[][], currentValue) => {
      if (
        accumulator.length === 0 ||
        accumulator[accumulator.length - 1].length >= chunk
      ) {
        accumulator.push([]);
      }
      accumulator[accumulator.length - 1].push(currentValue);
      return accumulator;
    }, []);
    return chunked;
  };

  const block = 'grid';
  let extraGridClasses = '';
  const gutterClasses = `${block}__gutter-${gutter} ${block}__gutter-${tabletGutter}-tablet ${block}__gutter-${mobileGutter}-mobile`;
  let layoutGuts = null;
  const childrenArray = Array.isArray(children) ? children : [children];

  if (layout === gridLayout.hub) {
    const chunks = chunkArray(childrenArray, 4) as JSX.Element[][];
    layoutGuts = chunks.map((chunk, i) => {
      extraGridClasses = chunk.every(
        item => item.props[dataClassName] === chunk[0].props[dataClassName]
      )
        ? chunk[0].props[dataClassName]
        : '';
      const [soloItem, ...groupedItems] = chunk;
      const groupedItemsLength = groupedItems.length;
      let groupGuts = null;

      const soloJsx = (
        <div key="solo" className={`${block}__contents`}>
          {soloItem}
        </div>
      );

      if (groupedItemsLength === 1) {
        groupGuts = (
          <div className={`${block}__contents`}>{groupedItems[0]}</div>
        );
        // isWrapper = false;
      } else if (groupedItemsLength === 2) {
        groupGuts = (
          <>
            <div className={`${block}__style-long ${gutterClasses}`}>
              <div className={`${block}__contents`}>{groupedItems[0]}</div>
            </div>
            <div className={`${block}__style-long`}>
              <div className={`${block}__contents`}>{groupedItems[1]}</div>
            </div>
          </>
        );
      } else if (groupedItemsLength === 3) {
        groupGuts = (
          <>
            <div className={`${block}__style-long ${gutterClasses}`}>
              <div className={`${block}__contents`}>{groupedItems[0]}</div>
            </div>
            <Grid
              gutter={gutter}
              layout={gridLayout.halves}
              tabletGutter={tabletGutter}
            >
              <div className={`${block}__contents ${block}__style-small`}>
                {groupedItems[1]}
              </div>
              <div className={`${block}__contents ${block}__style-small`}>
                {groupedItems[2]}
              </div>
            </Grid>
          </>
        );
      }

      const groupedItemsJsx = (
        <div key="grouped" className={`${block}__contents`}>
          {groupGuts}
        </div>
      );

      return (
        <Grid
          key={i}
          className={extraGridClasses}
          gutter={gutter}
          layout={gridLayout.halves}
          mobileGutter={mobileGutter}
          tabletGutter={tabletGutter}
        >
          {i % 2 === 0
            ? [soloJsx, groupedItemsJsx]
            : [groupedItemsJsx, soloJsx]}
        </Grid>
      );
    });
  } else if (layout === gridLayout.hubHero) {
    const chunks = chunkArray(childrenArray, 3) as JSX.Element[][];
    layoutGuts = chunks.map((chunk, i) => {
      extraGridClasses = chunk.every(
        item => item.props[dataClassName] === chunk[0].props[dataClassName]
      )
        ? chunk[0].props[dataClassName]
        : '';
      return (
        <Fragment key={i}>
          <div
            className={`${block}__three-quarter ${block}__style-giant ${gutterClasses} ${extraGridClasses}`}
          >
            <div className={`${block}__contents`}>{chunk[0]}</div>
          </div>
          <div className={`${block}__quarter {extraGridClasses}`}>
            {chunk.length > 1 && (
              <div className={`${block}__style-small ${gutterClasses}`}>
                <div className={`${block}__contents`}>{chunk[1]}</div>
              </div>
            )}
            {chunk.length > 2 && (
              <div className={`${block}__style-small`}>
                <div className={`${block}__contents`}>{chunk[2]}</div>
              </div>
            )}
          </div>
        </Fragment>
      );
    });
  } else {
    layoutGuts = childrenArray.map((child, i) => {
      if (isValidElement(child)) {
        extraGridClasses = child.props[dataClassName] || '';
      }

      return (
        <div
          key={i}
          className={`${block}__contents ${block}__h-gutter-${gutter} ${block}__h-gutter-${tabletGutter}-tablet ${block}__h-gutter-${mobileGutter}-mobile ${block}__v-gutter-${vgutter} ${extraGridClasses}`}
        >
          {child}
        </div>
      );
    });
  }

  return (
    <div
      className={`${block} ${block}--layout-${layout} ${block}--layout-${tabletLayout}-tablet ${block}--layout-${mobileLayout}-mobile ${block}--gutter-${gutter} ${block}--gutter-${tabletGutter}-tablet ${block}--gutter-${mobileGutter}-mobile ${block}--v-gutter-${vgutter} ${className ||
        ''}`}
    >
      {layoutGuts}
    </div>
  );
};

export default Grid;
